import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/weather/area/api';
import PageHeader from '@components/common/PageHeader';
import {
    toastSuccess,
    toastWarning,
} from '@components/common/Toast/CustomToast';
import { Alert } from '@utils/alert';
import ActionButton from '@/components/common/ActionButton/ActionButton';
import TextInput from '@/components/common/TextInput';
import { WeatherAreaAddReq } from '@/api/weather/area/type';

const AreaAdd = () => {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        regionName1: '',
        regionName2: '',
        regionName3: '',
        longCode: '',
        shortCode: '',
        nx: '',
        ny: '',
        longitude: '',
        latitude: '',
    });

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // 유효성 검사
    const validateForm = (): boolean => {
        // DOM을 통해 각 input의 유효성 검사
        const inputs = document.querySelectorAll('[data-required="true"]');

        for (const input of Array.from(inputs)) {
            const isValid = input.getAttribute('data-valid') === 'true';
            if (!isValid) {
                // 첫 번째 유효하지 않은 필드로 포커스
                (input as HTMLInputElement).focus();
                return false;
            }
        }

        return true;
    };

    // 등록 핸들러
    const handleSave = async () => {
        // 유효성 검사
        const isValid = validateForm();
        if (!isValid) {
            toastWarning('입력값을 확인해주세요.');
            return;
        }

        await Alert.confirmAndExecute(
            '기상대 지역을 등록하시겠습니까?',
            '입력한 정보를 다시 한번 확인해주세요.',
            true,
            async () => {
                const addRequest: WeatherAreaAddReq = {
                    regionName1: formData.regionName1,
                    regionName2: formData.regionName2,
                    regionName3: formData.regionName3,
                    longCode: formData.longCode || undefined,
                    shortCode: formData.shortCode,
                    nx: formData.nx,
                    ny: formData.ny,
                    longitude: formData.longitude,
                    latitude: formData.latitude,
                };

                await api.weatherAreaAdd(addRequest).then(() => {
                    toastSuccess('기상대 지역이 성공적으로 등록되었습니다.');
                    handleCancel();
                });
            },
        );
    };

    const handleCancel = (): void => {
        navigate('/weather/area');
    };

    return (
        <>
            <PageHeader
                title="기상 지역코드 등록"
                breadcrumbs={[
                    { label: '홈', href: '/' },
                    { label: '기상관리' },
                    { label: '기상 지역코드 관리' },
                    { label: '기상 지역코드 등록', active: true },
                ]}
            />

            <div className="card custom-card mt-4">
                <div className="card-header">
                    <div className="card-title">
                        기상대 지역 정보를 입력해주세요.
                    </div>
                </div>
                <div className="card-body">
                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                시,도<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <TextInput
                                value={formData.regionName1}
                                onChange={value =>
                                    handleInputChange('regionName1', value)
                                }
                                placeholder="시,도를 입력해주세요."
                                required={true}
                                label="시,도"
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                구,군<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <TextInput
                                value={formData.regionName2}
                                onChange={value =>
                                    handleInputChange('regionName2', value)
                                }
                                placeholder="구,군을 입력해주세요."
                                required={true}
                                label="구,군"
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                동,읍,면<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <TextInput
                                value={formData.regionName3}
                                onChange={value =>
                                    handleInputChange('regionName3', value)
                                }
                                placeholder="동,읍,면을 입력해주세요."
                                required={true}
                                label="동,읍,면"
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                중기지역코드 :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <TextInput
                                value={formData.longCode}
                                onChange={value =>
                                    handleInputChange('longCode', value)
                                }
                                placeholder="중기지역코드를 입력해주세요."
                                required={false}
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                단기지역코드
                                <span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <TextInput
                                value={formData.shortCode}
                                onChange={value =>
                                    handleInputChange('shortCode', value)
                                }
                                placeholder="단기지역코드를 입력해주세요."
                                required={true}
                                label="단기지역코드"
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                격자X<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <TextInput
                                value={formData.nx}
                                onChange={value =>
                                    handleInputChange('nx', value)
                                }
                                placeholder="격자X를 입력해주세요."
                                required={true}
                                label="격자X"
                                pattern="^\d+$"
                                errorMessage="숫자만 입력 가능합니다."
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                격자Y<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <TextInput
                                value={formData.ny}
                                onChange={value =>
                                    handleInputChange('ny', value)
                                }
                                placeholder="격자Y를 입력해주세요."
                                required={true}
                                label="격자Y"
                                pattern="^\d+$"
                                errorMessage="숫자만 입력 가능합니다."
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                경도<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <TextInput
                                value={formData.longitude}
                                onChange={value =>
                                    handleInputChange('longitude', value)
                                }
                                placeholder="경도를 입력해주세요."
                                required={true}
                                label="경도"
                                pattern="^-?\d+(\.\d+)?$"
                                errorMessage="올바른 경도 형식을 입력해주세요."
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-2">
                            <label className="form-label mb-md-0 mt-2">
                                위도<span className="text-danger">*</span> :
                            </label>
                        </div>
                        <div className="col-md-10">
                            <TextInput
                                value={formData.latitude}
                                onChange={value =>
                                    handleInputChange('latitude', value)
                                }
                                placeholder="위도를 입력해주세요."
                                required={true}
                                label="위도"
                                pattern="^-?\d+(\.\d+)?$"
                                errorMessage="올바른 위도 형식을 입력해주세요."
                            />
                        </div>
                    </div>
                </div>

                <div className="card-footer">
                    <div className="btn-list text-end">
                        <ActionButton type="save" onClick={handleSave} />
                        <ActionButton type="cancel" onClick={handleCancel} />
                    </div>
                </div>
            </div>
        </>
    );
};

export default AreaAdd;
